import { ECardinalityConversion, ESymbolCategory, ISymbol } from "@kuika/kuika-cl-model";
import { CardinalityConversion, decodeSymbolRef, SymbolRefParsed } from "./symbol-ref";

/**
 * Thrown when a symbolRef cannot be converted to an ISymbol — either the ref
 * is malformed, names an unknown kind, or points at a kind that doesn't
 * correspond to a value binding (notably `actionDef:` refs, which are event
 * attachments, not symbols).
 */
export class SymbolResolutionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SymbolResolutionError";
  }
}

// ECardinalityConversion also has `all | self | asArray`, but symbolRef grammar
// deliberately exposes only the three single-row selectors the picker surfaces
// as leaf nodes — the rest are intermediate cascader categories, not pickable.
const CONVERSION_MAP: Record<CardinalityConversion, ECardinalityConversion> = {
  first: ECardinalityConversion.first,
  last: ECardinalityConversion.last,
  selected: ECardinalityConversion.selected
};

const baseSymbol = (category: ESymbolCategory, label: string, description: string): ISymbol => ({
  category,
  label,
  description
});

const resolveParsed = (parsed: SymbolRefParsed): ISymbol => {
  switch (parsed.kind) {
    case "default":
      return {
        ...baseSymbol(ESymbolCategory.default, parsed.name, parsed.name),
        defaultValueName: parsed.name
      };
    case "screenInput":
      return {
        ...baseSymbol(ESymbolCategory.screenInput, parsed.name, parsed.name),
        inputName: parsed.name
      };
    case "di": {
      const diId = Number.parseInt(parsed.kuikaId, 10);
      if (Number.isNaN(diId)) {
        throw new SymbolResolutionError(`di ref has non-numeric kuikaId: ${parsed.kuikaId}`);
      }
      const symbol: ISymbol = {
        ...baseSymbol(ESymbolCategory.displayItemProp, parsed.propName, `${parsed.kuikaId}.${parsed.propName}`),
        diId,
        diPropName: parsed.propName
      };
      if (parsed.conversion) {
        symbol.converterOfCardinality = CONVERSION_MAP[parsed.conversion];
      }
      return symbol;
    }
    case "actionResult":
      return {
        ...baseSymbol(ESymbolCategory.actionResults, parsed.resultActionId, `[${parsed.resultActionId}]`),
        resultActionId: parsed.resultActionId
      };
    case "actionField":
      return {
        ...baseSymbol(ESymbolCategory.actionField, parsed.fieldPath, `[${parsed.resultActionId}].${parsed.fieldPath}`),
        resultActionId: parsed.resultActionId,
        fieldName: parsed.fieldPath
      };
    case "current":
      return {
        ...baseSymbol(ESymbolCategory.current, parsed.fieldPath, `current.${parsed.fieldPath}`),
        fieldName: parsed.fieldPath
      };
    case "bag":
      return {
        ...baseSymbol(ESymbolCategory.bag, parsed.bagName, parsed.bagName),
        bagName: parsed.bagName
      };
    case "actionDef":
      throw new SymbolResolutionError(
        `actionDef refs are event attachments, not value bindings — hand them to ui_bind.events, not resolveSymbolFromRef`
      );
  }
};

export const resolveSymbolFromRef = (ref: string): ISymbol => {
  let parsed: SymbolRefParsed;
  try {
    parsed = decodeSymbolRef(ref);
  } catch (e) {
    throw new SymbolResolutionError((e as Error).message);
  }
  return resolveParsed(parsed);
};

export const resolveSymbolFromLiteral = (value: unknown): ISymbol => {
  const fixedValue = value === null || value === undefined ? "" : value;
  return {
    category: ESymbolCategory.fixed,
    label: String(fixedValue),
    description: String(fixedValue),
    isFixed: true,
    fixedValue
  };
};

export const isActionDefBinding = (ref: string): boolean => ref.startsWith("actionDef:");
